import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Clock, MapPin, User, Mail, Phone as PhoneIcon, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import dayjs from 'dayjs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicBooking = () => {
  const { userId, slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: select time, 2: fill form, 3: confirmation
  const [eventType, setEventType] = useState(null);
  const [host, setHost] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [clientTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    notes: '',
    customAnswers: []
  });

  useEffect(() => {
    loadEventType();
  }, [userId, slug]);

  useEffect(() => {
    if (eventType) {
      loadAvailability();
    }
  }, [eventType]);

  useEffect(() => {
    if (selectedDate && allSlots.length > 0) {
      filterSlotsForDate(selectedDate);
    }
  }, [selectedDate, allSlots]);

  const loadEventType = async () => {
    try {
      const response = await axios.get(`${API}/public/event-types/${userId}/${slug}`);
      setEventType(response.data.eventType);
      setHost(response.data.host);
      
      // Initialize custom answers
      const answers = (response.data.eventType.customQuestions || []).map(q => ({
        questionId: q.id,
        question: q.question,
        answer: ''
      }));
      setFormData(prev => ({ ...prev, customAnswers: answers }));
    } catch (error) {
      toast.error('Event type not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailability = async () => {
    try {
      const start = dayjs().format('YYYY-MM-DD');
      const end = dayjs().add(60, 'days').format('YYYY-MM-DD');
      
      const response = await axios.get(
        `${API}/public/event-types/${userId}/${slug}/availability`,
        { params: { start_date: start, end_date: end, client_tz: clientTz } }
      );
      
      setAllSlots(response.data.slots || []);
    } catch (error) {
      toast.error('Failed to load availability');
    }
  };

  const filterSlotsForDate = (date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const filtered = allSlots.filter(slot => slot.date === dateStr);
    setAvailableSlots(filtered);
  };

  const tileDisabled = ({ date }) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    return !allSlots.some(slot => slot.date === dateStr);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomAnswerChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      customAnswers: prev.customAnswers.map(a =>
        a.questionId === questionId ? { ...a, answer: value } : a
      )
    }));
  };

  const handleNext = () => {
    if (step === 1 && !selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.clientName || !formData.clientEmail) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate custom questions
    const requiredQuestions = eventType.customQuestions?.filter(q => q.required) || [];
    for (const q of requiredQuestions) {
      const answer = formData.customAnswers.find(a => a.questionId === q.id);
      if (!answer || !answer.answer) {
        toast.error(`Please answer: ${q.question}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        eventTypeId: eventType.id,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientPhone: formData.clientPhone,
        clientTimezone: clientTz,
        scheduledTime: selectedSlot.datetime,
        notes: formData.notes,
        customAnswers: formData.customAnswers
      });

      setBooking(response.data.booking);
      setStep(3);
      toast.success('Meeting booked successfully!');
    } catch (error) {
      toast.error('Failed to book meeting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!eventType) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {host.profilePicUrl && (
            <img
              src={host.profilePicUrl}
              alt={host.name}
              className="w-20 h-20 rounded-full mx-auto mb-4 object-cover"
            />
          )}
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {eventType.name}
          </h1>
          <p className="text-slate-600 mb-4">with {host.name}</p>
          {eventType.description && (
            <p className="text-slate-600 max-w-2xl mx-auto">{eventType.description}</p>
          )}
          
          <div className="flex items-center justify-center gap-6 mt-4 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{eventType.duration} min</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span className="capitalize">{eventType.location.replace('_', ' ')}</span>
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
              {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
            </div>
            <div className={`h-1 w-16 ${step >= 2 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
              {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : '2'}
            </div>
            <div className={`h-1 w-16 ${step >= 3 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
              {step > 3 ? <CheckCircle2 className="w-5 h-5" /> : '3'}
            </div>
          </div>
        </div>

        {/* Step 1: Select Time */}
        {step === 1 && (
          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-6">Select a Date & Time</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Calendar */}
              <div>
                <Calendar
                  onChange={handleDateSelect}
                  value={selectedDate}
                  minDate={new Date()}
                  tileDisabled={tileDisabled}
                  className="border-0 shadow-sm rounded-lg"
                />
              </div>

              {/* Time Slots */}
              <div>
                {!selectedDate ? (
                  <div className="text-center text-slate-500 py-12">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Select a date to see available times</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center text-slate-500 py-12">
                    <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No available times on this date</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-600 mb-4">
                      {dayjs(selectedDate).format('dddd, MMMM D, YYYY')}
                    </p>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => handleSlotSelect(slot)}
                          className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                            selectedSlot?.datetime === slot.datetime
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                          }`}
                        >
                          {slot.client_time}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button
                onClick={handleNext}
                disabled={!selectedSlot}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Fill Form */}
        {step === 2 && (
          <Card className="p-8">
            <div className="mb-6">
              <button
                onClick={() => setStep(1)}
                className="flex items-center text-slate-600 hover:text-slate-900 mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <h2 className="text-2xl font-semibold mb-2">Enter Details</h2>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CalendarIcon className="w-4 h-4" />
                <span>{dayjs(selectedSlot.client_datetime).format('dddd, MMMM D, YYYY')}</span>
                <span>at</span>
                <Clock className="w-4 h-4" />
                <span>{selectedSlot.client_time}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="clientName">Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <Input
                      id="clientName"
                      value={formData.clientName}
                      onChange={(e) => handleChange('clientName', e.target.value)}
                      placeholder="Your full name"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="clientEmail">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <Input
                      id="clientEmail"
                      type="email"
                      value={formData.clientEmail}
                      onChange={(e) => handleChange('clientEmail', e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="clientPhone">Phone</Label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <Input
                      id="clientPhone"
                      type="tel"
                      value={formData.clientPhone}
                      onChange={(e) => handleChange('clientPhone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Custom Questions */}
              {eventType.customQuestions && eventType.customQuestions.length > 0 && (
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold">Additional Information</h3>
                  {eventType.customQuestions.map((question, index) => {
                    const answer = formData.customAnswers.find(a => a.questionId === question.id);
                    return (
                      <div key={question.id}>
                        <Label htmlFor={`question-${index}`}>
                          {question.question} {question.required && '*'}
                        </Label>
                        {question.type === 'textarea' ? (
                          <Textarea
                            id={`question-${index}`}
                            value={answer?.answer || ''}
                            onChange={(e) => handleCustomAnswerChange(question.id, e.target.value)}
                            placeholder={question.placeholder}
                            required={question.required}
                            rows={3}
                          />
                        ) : question.type === 'select' ? (
                          <Select
                            value={answer?.answer || ''}
                            onValueChange={(v) => handleCustomAnswerChange(question.id, v)}
                            required={question.required}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {(question.options || []).map((option, i) => (
                                <SelectItem key={i} value={option}>{option}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            id={`question-${index}`}
                            type={question.type}
                            value={answer?.answer || ''}
                            onChange={(e) => handleCustomAnswerChange(question.id, e.target.value)}
                            placeholder={question.placeholder}
                            required={question.required}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Anything else you'd like us to know?"
                  rows={3}
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? 'Booking...' : 'Confirm Booking'}
              </Button>
            </form>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && booking && (
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h2>
            <p className="text-slate-600 mb-6">{eventType.confirmationMessage}</p>

            <Card className="p-6 bg-slate-50 mb-6 text-left">
              <h3 className="font-semibold mb-4">Meeting Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <CalendarIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium">{dayjs(booking.scheduledTime).format('dddd, MMMM D, YYYY')}</p>
                    <p className="text-slate-600">{dayjs(booking.scheduledTime).format('h:mm A')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                  <p>{booking.duration} minutes</p>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium capitalize">{booking.location.replace('_', ' ')}</p>
                    {booking.meetingLink && (
                      <a
                        href={booking.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Join meeting
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-400 mt-0.5" />
                  <p>{host.name}</p>
                </div>
              </div>
            </Card>

            <p className="text-sm text-slate-600">
              A confirmation email has been sent to {booking.clientEmail}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicBooking;
